import React, { useState } from 'react';
import { useRsvp } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_luxury_teddy_006',
  name: 'Luxury Teddy Blush',
  occasionType: 'BABY_SHOWER',
  description: 'An ultra-premium luxury baby shower invitation matching the stationery studio design standard, with arced headings, detailed gold Victorian borders, watercolor rose clusters, and a teddy bear family on clouds.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'luxury', 'watercolor', 'teddy-bear'],
  colorScheme: { primary: '#b76e79', accent: '#ca8a04', bg: '#fff5f6' },
};

export function LuxuryTeddyBlush({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Emma Harrington';
  const hostedBy = baby.hostedBy || data.hostName || 'Sarah & David Harrington';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = '25TH DECEMBER';
  let dayOfWeek = 'SATURDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '12:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'THE STARDUST PAVILION';
  const mapLink = baby.mapLink;
  const message = baby.message || 'Love you to the moon and back. Join us to celebrate the upcoming arrival of our little star!';
  const dressCode = baby.showDressCode !== false ? baby.dressCode : undefined;

  // Custom Form State
  const { state: rsvpState, errorMessage: rsvpError, submit: submitRsvp } = useRsvp(invitationId, isLive);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [headcount, setHeadcount] = useState(1);
  const [guestMessage, setGuestMessage] = useState('');

  const handleRsvpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitRsvp({
      guestName,
      guestEmail: guestEmail || undefined,
      status: 'ACCEPTED',
      headcount,
      message: guestMessage || undefined,
    });
  };

  return (
    <div className="min-h-screen bg-stone-100 py-4 px-2 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-luxury { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-luxury { font-family: 'Montserrat', sans-serif; }
        .font-cursive-luxury { font-family: 'Pinyon Script', cursive; }
        .font-title-luxury { font-family: 'Cinzel', serif; }
        
        .watercolor-bg-luxury {
          /* Multi-layer watercolor wash: cream core → barely-blush mid → soft dusty-rose edge */
          background:
            radial-gradient(ellipse at 28% 22%, rgba(255,244,246,0.50) 0%, transparent 52%),
            radial-gradient(ellipse at 72% 78%, rgba(250,220,224,0.38) 0%, transparent 52%),
            radial-gradient(ellipse at 55% 48%, rgba(255,254,255,0.48) 0%, transparent 60%),
            radial-gradient(ellipse at 50% 50%, #ffffff 0%, #fefcfc 14%, #fdf6f7 42%, #faecee 72%, #f5dfe2 100%);
          position: relative;
        }
        
        /* Pearlescent Shimmer Overlay */
        .pearl-shimmer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.42) 0%, transparent 58%),
            linear-gradient(118deg,
              rgba(255,255,255,0.50) 0%,
              rgba(255,235,240,0.22) 22%,
              rgba(255,255,255,0.02) 48%,
              rgba(255,230,238,0.18) 70%,
              rgba(255,255,255,0.42) 100%);
          mix-blend-mode: overlay;
          z-index: 2;
        }

        /* Soft vignette — darkens edges slightly for depth */
        .paper-vignette {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(ellipse at 50% 50%,
            transparent 42%,
            rgba(200,140,150,0.10) 68%,
            rgba(170,100,115,0.20) 100%);
          mix-blend-mode: multiply;
          z-index: 3;
        }

        /* Fine Paper Grain Texture */
        .paper-grain {
          position: absolute;
          inset: 0;
          pointer-events: none;
          mix-blend-mode: multiply;
          opacity: 0.20;
          z-index: 1;
        }

        /* Rose Gold Foil style */
        .rose-gold-foil-text {
          background: linear-gradient(135deg, #b36b76 0%, #e8b2bb 30%, #f7d5db 50%, #e8b2bb 70%, #b36b76 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.8));
        }

        /* Floating sparkles */
        .luxury-sparkle-pt {
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(183,110,121,0) 70%);
          filter: drop-shadow(0 0 5px #fda4af);
          animation: floatSparkle 8s infinite ease-in-out;
        }

        @keyframes floatSparkle {
          0%, 100% { transform: translateY(0) scale(0.8); opacity: 0.2; }
          50% { transform: translateY(-25px) scale(1.3); opacity: 0.95; }
        }

        /* Decorative vector element animation */
        .deco-twinkle-a { animation: decoTwinkle 7s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .deco-twinkle-b { animation: decoTwinkle 9s ease-in-out infinite 2.5s; transform-box: fill-box; transform-origin: center; }
        .deco-twinkle-c { animation: decoTwinkle 11s ease-in-out infinite 1s; transform-box: fill-box; transform-origin: center; }

        @keyframes decoTwinkle {
          0%, 100% { opacity: 0.12; transform: scale(0.75) rotate(0deg); }
          50% { opacity: 0.72; transform: scale(1.15) rotate(18deg); }
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[768px] aspect-[2/3] shadow-2xl rounded-2xl overflow-hidden watercolor-bg-luxury flex flex-col py-8 px-6 border-[5px] border-white/85">
        {/* Shimmer, Vignette & Paper Texture Layers */}
        <div className="pearl-shimmer" />
        <div className="paper-vignette" />
        <svg className="paper-grain" width="100%" height="100%">
          <filter id="linenNoiseLTB">
            <feTurbulence type="fractalNoise" baseFrequency="0.62" numOctaves="4" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.04 0" />
            <feComposite operator="in" in2="SourceGraphic" />
          </filter>
          <rect width="100%" height="100%" filter="url(#linenNoiseLTB)" />
        </svg>

        {/* Scattered Gold Sparkle Dots */}
        <div className="luxury-sparkle-pt top-[15%] left-[25%]" style={{ animationDelay: '0s' }} />
        <div className="luxury-sparkle-pt top-[28%] right-[18%]" style={{ animationDelay: '2s' }} />
        <div className="luxury-sparkle-pt top-[58%] left-[15%]" style={{ animationDelay: '4s' }} />
        <div className="luxury-sparkle-pt top-[78%] right-[22%]" style={{ animationDelay: '1s' }} />
        <div className="luxury-sparkle-pt top-[43%] right-[8%]" style={{ animationDelay: '3s' }} />
        <div className="luxury-sparkle-pt top-[66%] right-[10%]" style={{ animationDelay: '5s' }} />
        <div className="luxury-sparkle-pt top-[88%] left-[22%]" style={{ animationDelay: '2.5s' }} />
        <div className="luxury-sparkle-pt top-[92%] right-[28%]" style={{ animationDelay: '1.5s' }} />

        {/* Luxury Decorative Elements — Stars, Hearts, Petals, Glitter Dots */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-[5]"
          viewBox="0 0 1080 1920"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          {/* ── 4-point gold sparkle stars ── */}
          <g transform="translate(513, 112)" className="deco-twinkle-a">
            <path d="M 0 -14 L 5 -5 L 14 0 L 5 5 L 0 14 L -5 5 L -14 0 L -5 -5 Z"
                  fill="#ca8a04" opacity="0.58" />
          </g>
          <g transform="translate(78, 495)" className="deco-twinkle-b">
            <path d="M 0 -11 L 4 -4 L 11 0 L 4 4 L 0 11 L -4 4 L -11 0 L -4 -4 Z"
                  fill="#ca8a04" opacity="0.48" />
          </g>
          <g transform="translate(1004, 642)" className="deco-twinkle-c">
            <path d="M 0 -11 L 4 -4 L 11 0 L 4 4 L 0 11 L -4 4 L -11 0 L -4 -4 Z"
                  fill="#ca8a04" opacity="0.46" />
          </g>
          <g transform="translate(82, 1285)" className="deco-twinkle-b">
            <path d="M 0 -10 L 3.5 -3.5 L 10 0 L 3.5 3.5 L 0 10 L -3.5 3.5 L -10 0 L -3.5 -3.5 Z"
                  fill="#ca8a04" opacity="0.42" />
          </g>
          <g transform="translate(540, 1792)" className="deco-twinkle-a">
            <path d="M 0 -11 L 4 -4 L 11 0 L 4 4 L 0 11 L -4 4 L -11 0 L -4 -4 Z"
                  fill="#ca8a04" opacity="0.44" />
          </g>

          {/* ── Tiny blush pink hearts ── */}
          <g transform="translate(437, 170)" className="deco-twinkle-c">
            <path d="M 0 7.5 C -3 3, -8.5 0, -8.5 -4 C -8.5 -9.5, 0 -9.5, 0 -5.5 C 0 -9.5, 8.5 -9.5, 8.5 -4 C 8.5 0, 3 3, 0 7.5 Z"
                  fill="#f4a5b0" opacity="0.52" />
          </g>
          <g transform="translate(648, 150)" className="deco-twinkle-a">
            <path d="M 0 6.5 C -2.5 2.5, -7 0, -7 -3.5 C -7 -7.5, 0 -7.5, 0 -4.5 C 0 -7.5, 7 -7.5, 7 -3.5 C 7 0, 2.5 2.5, 0 6.5 Z"
                  fill="#f4a5b0" opacity="0.46" />
          </g>
          <g transform="translate(84, 874)" className="deco-twinkle-b">
            <path d="M 0 6 C -2.5 2.5, -7 0, -7 -3.5 C -7 -7.5, 0 -7.5, 0 -4.5 C 0 -7.5, 7 -7.5, 7 -3.5 C 7 0, 2.5 2.5, 0 6 Z"
                  fill="#e8899a" opacity="0.40" />
          </g>
          <g transform="translate(998, 972)" className="deco-twinkle-c">
            <path d="M 0 6 C -2.5 2.5, -7 0, -7 -3.5 C -7 -7.5, 0 -7.5, 0 -4.5 C 0 -7.5, 7 -7.5, 7 -3.5 C 7 0, 2.5 2.5, 0 6 Z"
                  fill="#e8899a" opacity="0.40" />
          </g>

          {/* ── Rose-gold glitter dots ── */}
          <circle cx="558" cy="90" r="3.5" fill="#c9907a" opacity="0.50" />
          <circle cx="96" cy="662" r="3" fill="#c9907a" opacity="0.42" />
          <circle cx="985" cy="422" r="3" fill="#c9907a" opacity="0.42" />
          <circle cx="478" cy="1842" r="3.5" fill="#c9907a" opacity="0.44" />
          <circle cx="602" cy="1822" r="2.5" fill="#c9907a" opacity="0.36" />
          <circle cx="1006" cy="1202" r="3" fill="#c9907a" opacity="0.38" />
          <circle cx="74" cy="1120" r="3" fill="#c9907a" opacity="0.38" />

          {/* ── Soft watercolor petal ellipses ── */}
          <ellipse cx="106" cy="1182" rx="6" ry="14"
                   transform="rotate(30, 106, 1182)" fill="#fda4af" opacity="0.30" />
          <ellipse cx="976" cy="1382" rx="5" ry="12"
                   transform="rotate(-25, 976, 1382)" fill="#fda4af" opacity="0.28" />
          <ellipse cx="96" cy="402" rx="4" ry="11"
                   transform="rotate(20, 96, 402)" fill="#fda4af" opacity="0.28" />
        </svg>

        {/* Premium Rose-Gold Ornamental Border */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-10"
          viewBox="0 0 1080 1920"
          preserveAspectRatio="none"
          fill="none"
        >
          <defs>
            <linearGradient id="rgBorderLTB" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#b07060" />
              <stop offset="30%"  stopColor="#ce9070" />
              <stop offset="50%"  stopColor="#e8b490" />
              <stop offset="70%"  stopColor="#ce9070" />
              <stop offset="100%" stopColor="#b07060" />
            </linearGradient>
          </defs>

          {/* ── Outer border rect ── */}
          <rect x="40" y="40" width="1000" height="1840"
                stroke="url(#rgBorderLTB)" strokeWidth="2.8" opacity="0.88" />

          {/* ── Inner hair-line double ── */}
          <rect x="54" y="54" width="972" height="1812"
                stroke="url(#rgBorderLTB)" strokeWidth="1.1" opacity="0.50" />

          {/* ════ TOP-LEFT CORNER ════ */}
          <path d="M 40 27 L 53 40 L 40 53 L 27 40 Z"
                fill="url(#rgBorderLTB)" opacity="0.82" />
          <line x1="55" y1="40" x2="115" y2="40"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <line x1="40" y1="55" x2="40" y2="115"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <path d="M 98 40 C 89 40, 78 50, 78 62"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <path d="M 40 98 C 40 89, 50 78, 62 78"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <circle cx="115" cy="40" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />
          <circle cx="40" cy="115" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />

          {/* ════ TOP-RIGHT CORNER ════ */}
          <path d="M 1040 27 L 1053 40 L 1040 53 L 1027 40 Z"
                fill="url(#rgBorderLTB)" opacity="0.82" />
          <line x1="1025" y1="40" x2="965" y2="40"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <line x1="1040" y1="55" x2="1040" y2="115"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <path d="M 982 40 C 991 40, 1002 50, 1002 62"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <path d="M 1040 98 C 1040 89, 1030 78, 1018 78"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <circle cx="965" cy="40" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />
          <circle cx="1040" cy="115" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />

          {/* ════ BOTTOM-LEFT CORNER ════ */}
          <path d="M 40 1867 L 53 1880 L 40 1893 L 27 1880 Z"
                fill="url(#rgBorderLTB)" opacity="0.82" />
          <line x1="55" y1="1880" x2="115" y2="1880"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <line x1="40" y1="1865" x2="40" y2="1805"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <path d="M 98 1880 C 89 1880, 78 1870, 78 1858"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <path d="M 40 1822 C 40 1831, 50 1842, 62 1842"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <circle cx="115" cy="1880" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />
          <circle cx="40" cy="1805" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />

          {/* ════ BOTTOM-RIGHT CORNER ════ */}
          <path d="M 1040 1867 L 1053 1880 L 1040 1893 L 1027 1880 Z"
                fill="url(#rgBorderLTB)" opacity="0.82" />
          <line x1="1025" y1="1880" x2="965" y2="1880"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <line x1="1040" y1="1865" x2="1040" y2="1805"
                stroke="url(#rgBorderLTB)" strokeWidth="1.2" opacity="0.48" />
          <path d="M 982 1880 C 991 1880, 1002 1870, 1002 1858"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <path d="M 1040 1822 C 1040 1831, 1030 1842, 1018 1842"
                stroke="url(#rgBorderLTB)" strokeWidth="0.9" strokeLinecap="round" opacity="0.52" />
          <circle cx="965" cy="1880" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />
          <circle cx="1040" cy="1805" r="3.2" fill="url(#rgBorderLTB)" opacity="0.52" />

          {/* ── Mid-edge accent dots ── */}
          <circle cx="540"  cy="40"   r="2.5" fill="url(#rgBorderLTB)" opacity="0.42" />
          <circle cx="540"  cy="1880" r="2.5" fill="url(#rgBorderLTB)" opacity="0.42" />
          <circle cx="40"   cy="960"  r="2.5" fill="url(#rgBorderLTB)" opacity="0.42" />
          <circle cx="1040" cy="960"  r="2.5" fill="url(#rgBorderLTB)" opacity="0.42" />
        </svg>

        {/* Top-Left: Rich Luxury Watercolor Floral Arrangement */}
        <div className="absolute top-[-4%] left-[-4%] w-[45%] aspect-square pointer-events-none z-[3]">
          <img
            src="/assets/templates/luxury-teddy-blush/floral-top-left.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'fill', objectPosition: 'top left', opacity: 0.97 }}
          />
        </div>

        {/* Teddy Bear Family — Premium Watercolor Illustration */}
        <div className="absolute top-[1.5%] right-[1.5%] w-[35%] aspect-square pointer-events-none z-[4]">
          <img
            src="/assets/templates/luxury-teddy-blush/teddy-bear.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'fill', opacity: 0.97, filter: 'drop-shadow(0 6px 18px rgba(140,80,90,0.18))' }}
          />
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-[22%] pb-12 px-4 w-full max-w-[75%] mx-auto">
          
          {/* Header section — luxury stationery typography */}
          <div className="text-center w-full">

            {/* Subheading: small uppercase serif, very wide tracking */}
            <p className="font-title-luxury text-[16px] tracking-[0.45em] text-[#7A3D46] uppercase mb-4 break-words">
              PLEASE JOIN US FOR A
            </p>

            {/* Main Title: large luxury serif, dark rose */}
            <h2 className="font-title-luxury text-[70px] text-[#7A3D46] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-6 text-balance">
              BABY SHOWER
            </h2>

            {/* Tiny Heart Divider */}
            <div className="flex justify-center mb-6">
              <svg className="w-3 h-3 text-[#7A3D46]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            </div>

            {/* Hosted By / In Honor Of */}
            <span className="font-title-luxury text-[14px] text-[#8A6A6A] tracking-[0.42em] block mb-2 uppercase">
              IN HONOR OF
            </span>

            {/* Mother name — largest element, elegant Pinyon Script */}
            <div className="w-full mb-8 overflow-hidden flex justify-center">
              <h1 className="font-cursive-luxury text-[64px] text-[#7A3D46] leading-[1.3] text-balance max-w-full">
                {motherName}
              </h1>
            </div>
          </div>

            {/* Details Section — centered stationery layout */}
          <div className="flex flex-col items-center gap-6 text-center font-sans-luxury flex-grow justify-center w-full z-10">

            {/* Date, Time, Venue — With Icons */}
            <div className="w-full max-w-[100%] mx-auto">
              <div className="border-t border-b border-[#9B5060]/20 py-4 flex flex-row items-center justify-between">
                
                {/* Left: Date */}
                <div className="flex-1 flex flex-row items-center justify-center gap-3 px-2">
                  <svg className="w-6 h-6 text-[#7A3D46]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  <div className="text-left">
                    <span className="font-sans-luxury text-[11px] text-[#4B3A3A] font-semibold uppercase block">{dayOfWeek}</span>
                    <span className="font-sans-luxury text-[11px] text-[#4B3A3A] font-medium uppercase block">{formattedDate.replace(' ', ' ')}</span>
                  </div>
                </div>
                
                {/* Vertical divider */}
                <div className="w-px h-10 bg-[#9B5060]/20 shrink-0" />
                
                {/* Middle: Time */}
                <div className="flex-1 flex flex-row items-center justify-center gap-3 px-2">
                  <svg className="w-6 h-6 text-[#7A3D46]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  <div className="text-left">
                    <span className="font-sans-luxury text-[11px] text-[#4B3A3A] font-semibold uppercase block">AT {eventTime}</span>
                    <span className="font-sans-luxury text-[11px] text-[#4B3A3A] font-medium uppercase block">ONWARDS</span>
                  </div>
                </div>

                {/* Vertical divider */}
                <div className="w-px h-10 bg-[#9B5060]/20 shrink-0" />

                {/* Right: Venue */}
                <div className="flex-1 flex flex-row items-center justify-center gap-3 px-2">
                  {mapLink ? (
                    <a href={mapLink} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                      <svg className="w-6 h-6 text-[#7A3D46]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    </a>
                  ) : (
                    <svg className="w-6 h-6 text-[#7A3D46]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  )}
                  <div className="text-left">
                    <span className="font-sans-luxury text-[11px] text-[#4B3A3A] font-semibold uppercase block truncate max-w-[120px]">{venueName}</span>
                    {mapLink && (
                      <a href={mapLink} target="_blank" rel="noopener noreferrer" className="font-sans-luxury text-[10px] text-[#4B3A3A] font-medium block truncate max-w-[120px] hover:underline">View Map</a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dress code — elegant bordered section without tag */}
            {dressCode && (
              <div className="border-b border-[#9B5060]/20 pb-4 w-full">
                <span className="font-title-luxury text-[13px] tracking-[0.2em] text-[#7A3D46] uppercase font-semibold block mb-1">
                  DRESS CODE
                </span>
                <span className="font-sans-luxury text-[14px] text-[#4B3A3A]">
                  {dressCode}
                </span>
              </div>
            )}

            {/* Bottom Quote */}
            <div className="text-center w-full mt-2">
              <span className="font-serif-luxury text-[18px] text-[#4B3A3A] block leading-relaxed max-w-[90%] mx-auto italic font-medium">
                "{message}"
              </span>
            </div>

            {/* RSVP */}
            <div className="mt-4 mb-2">
              <button
                type="button"
                aria-label="RSVP Now"
                onClick={() => setShowRsvpModal(true)}
                className="px-8 py-3 rounded-full bg-[#7A3D46] text-white flex items-center justify-center gap-2 shadow-[0_4px_15px_rgba(122,61,70,0.25)] hover:bg-[#68333b] hover:shadow-[0_6px_20px_rgba(122,61,70,0.35)] transition-all duration-300 group"
              >
                <svg className="w-4 h-4 text-white group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                <span className="font-sans-luxury text-[14px] tracking-[0.1em] font-semibold uppercase block">RSVP NOW</span>
              </button>
            </div>

          </div>



        </div>

        {/* Bottom-Right: Rich Luxury Watercolor Floral Arrangement */}
        <div className="absolute bottom-[-4%] right-[-4%] w-[45%] aspect-square pointer-events-none z-[3]">
          <img
            src="/assets/templates/luxury-teddy-blush/floral-bottom-right.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'fill', objectPosition: 'bottom right', opacity: 0.97 }}
          />
        </div>

      </div>

      {/* Custom Light RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-900/40 backdrop-blur-md transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-[440px] bg-white border border-[#9B5060]/20 rounded-[2rem] p-8 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-5 right-5 text-stone-400 hover:text-stone-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-3xl font-title-luxury text-[#7A3D46] font-semibold mb-2">RSVP</h3>
              <p className="text-[#4B3A3A]/70 text-sm font-sans-luxury">Please fill in your details to RSVP</p>
            </div>

            {rsvpState === 'success' ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">✨</div>
                <p className="text-[#7A3D46] font-semibold text-xl mb-2 font-serif-luxury">Thank You!</p>
                <p className="text-[#4B3A3A]/70 text-sm font-sans-luxury">Your response has been saved.</p>
              </div>
            ) : data.rsvpLink && data.rsvpLink !== '#' ? (
              <div className="text-center py-6">
                <a
                  href={data.rsvpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#7A3D46] text-white font-semibold rounded-xl hover:bg-[#68333b] transition-all text-sm font-sans-luxury"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  Open RSVP Form
                </a>
              </div>
            ) : (
              <form onSubmit={handleRsvpSubmit} className="flex flex-col gap-4 font-sans-luxury">
                {/* Full Name */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                  <input type="text" placeholder="Full Name *" required value={guestName} onChange={e => setGuestName(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#9B5060]/20 bg-[#faf6f7] text-[#4B3A3A] placeholder:text-[#8A6A6A]/60 focus:outline-none focus:border-[#7A3D46]/50 focus:bg-white transition-colors text-sm" />
                </div>
                
                {/* Email Address */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <input type="email" placeholder="Email Address *" required value={guestEmail} onChange={e => setGuestEmail(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#9B5060]/20 bg-[#faf6f7] text-[#4B3A3A] placeholder:text-[#8A6A6A]/60 focus:outline-none focus:border-[#7A3D46]/50 focus:bg-white transition-colors text-sm" />
                </div>

                {/* Phone Number */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <input type="tel" placeholder="Phone Number *" required value={guestPhone} onChange={e => setGuestPhone(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#9B5060]/20 bg-[#faf6f7] text-[#4B3A3A] placeholder:text-[#8A6A6A]/60 focus:outline-none focus:border-[#7A3D46]/50 focus:bg-white transition-colors text-sm" />
                </div>

                {/* Number of Guests */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  </div>
                  <select value={headcount} onChange={e => setHeadcount(Number(e.target.value))} className="w-full pl-12 pr-10 py-3 rounded-xl border border-[#9B5060]/20 bg-[#faf6f7] text-[#4B3A3A] focus:outline-none focus:border-[#7A3D46]/50 focus:bg-white transition-colors text-sm appearance-none cursor-pointer">
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>

                {/* Any Message */}
                <div className="relative">
                  <div className="absolute top-3 left-0 pl-4 flex items-start pointer-events-none">
                    <svg className="w-5 h-5 text-[#8A6A6A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                  </div>
                  <textarea placeholder="Any Message (Optional)" value={guestMessage} onChange={e => setGuestMessage(e.target.value)} rows={3} className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#9B5060]/20 bg-[#faf6f7] text-[#4B3A3A] placeholder:text-[#8A6A6A]/60 focus:outline-none focus:border-[#7A3D46]/50 focus:bg-white transition-colors text-sm resize-none" />
                </div>

                {rsvpError && <p className="text-red-500 text-xs text-center">{rsvpError}</p>}

                <button type="submit" disabled={rsvpState === 'loading'} className="w-full mt-2 py-4 rounded-full bg-[#7A3D46] text-white font-semibold text-sm tracking-widest uppercase hover:bg-[#68333b] disabled:opacity-50 transition-colors shadow-lg">
                  {rsvpState === 'loading' ? 'SUBMITTING...' : 'SUBMIT RSVP'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: LuxuryTeddyBlush });
